<script setup>
import { ref } from 'vue'
import { useUserStore } from '../stores/userStore'
import UserCard from '../components/UserCard.vue'
import StatCard from '../components/StatCard.vue'
import AddClientModal from '../components/AddClientModal.vue'

const userStore = useUserStore()
const clients = userStore.getByRole('client')

const stats = {
  totalClients: {
    value: clients.length,
    change: 15,
    trend: 'up',
    icon: 'users'
  },
  activeClients: {
    value: clients.filter(c => c.status === 'active').length,
    change: 5,
    trend: 'up',
    icon: 'user-check'
  },
  apiUsage: {
    value: 85,
    change: -2,
    trend: 'down',
    icon: 'chart-line'
  }
}

const searchQuery = ref('')
const filters = ref({
  status: 'all',
  accessLevel: 'all'
})

const viewMode = ref('grid') // 'grid' or 'list'

const toggleView = (mode) => {
  viewMode.value = mode
}

const showAddClientModal = ref(false)
</script>

<template>
  <div class="p-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold">Clients</h1>
        <p class="text-gray-500">Dashboard / Clients</p>
      </div>
      
      <div class="flex gap-3">
        <button class="btn-secondary">
          <i class="fas fa-filter mr-2"></i> Filter
        </button>
        <button class="btn-secondary">
          <i class="fas fa-download mr-2"></i> Export
        </button>
        <button 
          @click="showAddClientModal = true"
          class="btn-primary bg-pink-500 hover:bg-pink-600"
        >
          + Add Client
        </button>
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-3 gap-6 mb-8">
      <StatCard
        v-for="(stat, key) in stats"
        :key="key"
        :title="key.replace(/([A-Z])/g, ' $1').trim()"
        :value="stat.value"
        :change="stat.change"
        :icon="stat.icon"
        period="vs last month"
      />
    </div>

    <!-- Search and Filters -->
    <div class="flex gap-4 mb-6">
      <div class="flex-1 relative">
        <input 
          v-model="searchQuery"
          type="text"
          placeholder="Search clients..."
          class="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
        >
        <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
      </div>
      
      <select 
        v-model="filters.status"
        class="px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
      >
        <option value="all">All Status</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>
      
      <select 
        v-model="filters.accessLevel"
        class="px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
      >
        <option value="all">All Access Levels</option>
        <option value="basic">Basic</option>
        <option value="full">Full</option>
        <option value="advanced">Advanced</option>
      </select>
    </div>

    <!-- View Toggle -->
    <div class="flex justify-end mb-4">
      <div class="bg-gray-100 rounded-lg p-1">
        <button 
          @click="toggleView('grid')"
          class="px-3 py-1 rounded-lg"
          :class="viewMode === 'grid' ? 'bg-white shadow' : ''"
        >
          <i class="fas fa-th-large"></i>
        </button>
        <button 
          @click="toggleView('list')"
          class="px-3 py-1 rounded-lg"
          :class="viewMode === 'list' ? 'bg-white shadow' : ''"
        >
          <i class="fas fa-list"></i>
        </button>
      </div>
    </div>

    <!-- Grid View -->
    <div v-if="viewMode === 'grid'" class="grid grid-cols-3 gap-6">
      <UserCard 
        v-for="client in clients"
        :key="client.id"
        :user="client"
      />
    </div>

    <!-- List View -->
    <div v-else class="bg-white rounded-lg shadow">
      <table class="w-full">
        <thead class="bg-gray-800 text-white">
          <tr>
            <th class="py-3 px-4 text-left">User</th>
            <th class="py-3 px-4 text-left">Status</th>
            <th class="py-3 px-4 text-left">API Key</th>
            <th class="py-3 px-4 text-left">Access Level</th>
            <th class="py-3 px-4 text-left">Last Active</th>
            <th class="py-3 px-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y">
          <tr v-for="client in clients" :key="client.id" class="hover:bg-gray-50">
            <td class="py-3 px-4">
              <div class="flex items-center">
                <div class="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <i class="fas fa-user text-gray-500"></i>
                </div>
                <div class="ml-3">
                  <div class="font-medium">{{ client.name }}</div>
                  <div class="text-sm text-gray-500">{{ client.email }}</div>
                </div>
              </div>
            </td>
            <td class="py-3 px-4">
              <span 
                class="px-2 py-1 rounded-full text-sm"
                :class="client.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'"
              >
                {{ client.status }}
              </span>
            </td>
            <td class="py-3 px-4 font-mono text-sm">{{ client.apiKey }}</td>
            <td class="py-3 px-4 capitalize">{{ client.accessLevel }}</td>
            <td class="py-3 px-4 text-sm text-gray-500">{{ client.lastActive }}</td>
            <td class="py-3 px-4">
              <div class="flex justify-end gap-2">
                <button class="text-gray-400 hover:text-gray-600">
                  <i class="fas fa-eye"></i>
                </button>
                <button class="text-gray-400 hover:text-gray-600">
                  <i class="fas fa-edit"></i>
                </button>
                <button class="text-gray-400 hover:text-red-600">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <AddClientModal 
    :show="showAddClientModal"
    @close="showAddClientModal = false"
  />
</template>

<style scoped>
.btn-primary {
  @apply px-4 py-2 text-white rounded-lg transition-colors duration-200;
}

.btn-secondary {
  @apply px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg 
         hover:bg-gray-50 transition-colors duration-200;
}
</style> 